// Authentication-related helpers used by both frontend AND backend.
import { z } from 'zod';
import type { Stage } from './schemas/enums';

//
// Config
//

export const cognitoConfig = {
  dev: {
    userPoolId: 'ap-southeast-2_U9VUYuW6Y',
    userPoolClientId: '5f7bfmrs6rrvtraqlve8e07u2c',
  },
  production: {
    userPoolId: 'ap-southeast-2_ObnK6Renq',
    userPoolClientId: '2ldi04u73blccij8vtjjvgnq1p',
  },
  staging: {
    userPoolId: 'ap-southeast-2_U9VUYuW6Y',
    userPoolClientId: '5f7bfmrs6rrvtraqlve8e07u2c',
  },
};

/**
 * Convex throws an error if ENV variables are tried to be used in any imported files.
 * See https://discord.com/channels/1019350475847499849/1252573078848798721/1252574564479668236
 * To work around this, we wrap this logic in a function.
 */
export const getCognitoConfig = () => {
  // Set these values in a `.env.local` file in the root of the consuming application.
  const stage = (process.env.VITE_STAGE || process.env.EXPO_PUBLIC_STAGE) as Stage | undefined;

  // Throw instead of defaulting to 'dev' to avoid accidentally using dev settings.
  if (!stage) {
    throw new Error('VITE_STAGE or EXPO_PUBLIC_STAGE environment variable is not set');
  }

  console.log(`[auth] stage: ${stage}`);

  const config = cognitoConfig[stage];
  if (!config) {
    throw new Error(`Unrecognized STAGE value: ${stage}`);
  }

  return {
    Auth: {
      Cognito: config,
    },
  };
};

//
// Kind
//

/**
 * The only value Cognito's `custom:kind` may hold. The attribute is either
 * `'superAdmin'` or not set at all — an unset attribute means a standard user, so
 * there is no `'user'` value to store and a token without the claim fails to the
 * least privilege.
 */
export const cognitoKindSchema = z.literal('superAdmin');
export type CognitoKind = z.infer<typeof cognitoKindSchema>;

//
// User identity
//

const identitySchema = z.object({
  dateCreated: z.string(),
  issuer: z.string().nullable(),
  primary: z.string(),
  providerName: z.string(),
  providerType: z.string(),
  userId: z.string(),
});

export const cognitoUserIdentitySchema = z.object({
  tokenIdentifier: z.string().url(),
  issuer: z.string().url(),
  subject: z.string(),
  givenName: z.string().optional(),
  familyName: z.string().optional(),
  email: z.string().optional(),
  emailVerified: z.boolean().optional(),
  phoneNumber: z.string().optional(),
  phoneNumberVerified: z.boolean().optional(),
  identities: z.array(identitySchema).optional(),
  origin_jti: z.string(),
  token_use: z.string(),
  /** Maps to `cognito:groups` */
  groups: z.array(z.string()).optional(),
  /** Maps to `cognito:username` */
  username: z.string(),

  //
  // Custom attributes
  //

  /**
   * Maps to `custom:kind`. `undefined` ⇒ standard user; only `superAdmin`s carry the
   * claim. (Verified against the Cognito pool: an unset custom attribute is omitted
   * from the token, so the claim arrives as `undefined`.)
   *
   * Any other value — including the `'user'` this once allowed — is rejected rather
   * than coerced, so a role can never be granted by a typo.
   */
  kind: cognitoKindSchema.optional(),
});

export type CognitoUserIdentity = z.infer<typeof cognitoUserIdentitySchema>;
